/**
 * KnowledgePrimingDashboard.tsx
 *
 * Unified Knowledge Priming Console for Superadmins and Channel Admins.
 * Supports scoped document ingestion, chunk inspection, authorized retrieval testing,
 * and tombstone-safe index rebuilds.
 */

import React, { useState } from 'react';
import {
  globalRagIngestionService,
  type EmbeddingScope,
  type IngestionStatus,
} from '../../mesh/rag/rag-ingestion-service.js';

export const KnowledgePrimingDashboard: React.FC = () => {
  const [selectedScope, setSelectedScope] = useState<EmbeddingScope>('plugin');
  const [scopeId, setScopeId] = useState<string>('plugin:scrum');
  const [docTitle, setDocTitle] = useState<string>('');
  const [docContent, setDocContent] = useState<string>('');
  const [ingestStatus, setIngestStatus] = useState<string | null>(null);
  const [chunkCount, setChunkCount] = useState<number>(0);

  // Playground state
  const [testQuery, setTestQuery] = useState<string>('how to resolve blocker');
  const [searchResults, setSearchResults] = useState<any[]>([]);

  const rag = globalRagIngestionService;

  const handleIngest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!docTitle.trim() || !docContent.trim()) return;

    const res = await rag.ingestDocument({
      documentId: `doc-${Date.now()}`,
      scope: selectedScope,
      scopeId,
      tenantId: 'tenant-alpha',
      title: docTitle.trim(),
      content: docContent.trim(),
    });

    if (res.ok) {
      setIngestStatus(`Successfully ingested "${docTitle}" into ${scopeId} (${res.chunkCount} chunks indexed).`);
      setChunkCount(res.chunkCount);
      setDocTitle('');
      setDocContent('');
    } else {
      setIngestStatus(`Ingestion rejected: ${res.error}`);
    }
  };

  const handleTestQuery = () => {
    if (!testQuery.trim()) return;

    const res = rag.queryKnowledge({
      query: testQuery,
      tenantId: 'tenant-alpha',
      authorizedScopeIds: [scopeId, 'global'],
      topK: 3,
    });

    setSearchResults(res.chunks as any[]);
  };

  const handleReindex = () => {
    const stats = rag.rebuildIndices();
    setIngestStatus(
      `Re-indexing complete: ${stats.reindexedCount} documents indexed (${stats.excludedTombstones} tombstones & ${stats.excludedQuarantined} quarantined excluded).`
    );
  };

  return (
    <div
      style={{
        padding: '24px',
        backgroundColor: 'var(--theme-bg-main, #0b0f19)',
        color: 'var(--theme-text-primary, #f8fafc)',
        minHeight: '100%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 700 }}>📚 Knowledge Priming & Scoped RAG Console</h1>
          <p style={{ fontSize: '13px', color: 'var(--theme-text-secondary, #cbd5e1)', marginTop: '4px' }}>
            Author, prime, and verify scoped knowledge collections with automatic privacy sanitization.
          </p>
        </div>
        <button
          onClick={handleReindex}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--theme-bg-card, #151d2a)',
            border: '1px solid var(--theme-border-highlight, #38bdf8)',
            borderRadius: 'var(--theme-radius-md, 8px)',
            color: 'var(--theme-accent, #38bdf8)',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          🔄 Re-Index All (Tombstone-Safe)
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Left Column: Ingestion Form */}
        <div
          style={{
            backgroundColor: 'var(--theme-bg-card, #151d2a)',
            border: '1px solid var(--theme-border, #1e293b)',
            borderRadius: 'var(--theme-radius-lg, 12px)',
            padding: '20px',
          }}
        >
          <h2 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>1. Prime New Knowledge Document</h2>
          <form onSubmit={handleIngest} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
                  Scope Type
                </label>
                <select
                  value={selectedScope}
                  onChange={(e) => {
                    const sc = e.target.value as EmbeddingScope;
                    setSelectedScope(sc);
                    setScopeId(sc === 'global' ? 'global' : sc === 'plugin' ? 'plugin:scrum' : 'channel:private-01');
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: 'var(--theme-bg-input, #1e293b)',
                    border: '1px solid var(--theme-border, #1e293b)',
                    borderRadius: '6px',
                    color: 'var(--theme-text-primary, #f8fafc)',
                    fontSize: '12px',
                  }}
                >
                  <option value="global">Global Workspace</option>
                  <option value="plugin">Plugin Specific</option>
                  <option value="channel">Private Channel Vault</option>
                </select>
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
                  Target Scope ID
                </label>
                <input
                  type="text"
                  value={scopeId}
                  onChange={(e) => setScopeId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: 'var(--theme-bg-input, #1e293b)',
                    border: '1px solid var(--theme-border, #1e293b)',
                    borderRadius: '6px',
                    color: 'var(--theme-text-primary, #f8fafc)',
                    fontSize: '12px',
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
                Document Title
              </label>
              <input
                type="text"
                placeholder="e.g., Sprint Blocker Resolution Runbook"
                value={docTitle}
                onChange={(e) => setDocTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: 'var(--theme-bg-input, #1e293b)',
                  border: '1px solid var(--theme-border, #1e293b)',
                  borderRadius: '6px',
                  color: 'var(--theme-text-primary, #f8fafc)',
                  fontSize: '12px',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
                Document Content (Markdown / Text)
              </label>
              <textarea
                rows={8}
                placeholder="Paste runbook text, technical specs, or policies here..."
                value={docContent}
                onChange={(e) => setDocContent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: 'var(--theme-bg-input, #1e293b)',
                  border: '1px solid var(--theme-border, #1e293b)',
                  borderRadius: '6px',
                  color: 'var(--theme-text-primary, #f8fafc)',
                  fontSize: '12px',
                  fontFamily: 'var(--theme-font-mono)',
                }}
              />
            </div>

            <button
              type="submit"
              style={{
                padding: '10px',
                backgroundColor: 'var(--theme-accent, #38bdf8)',
                color: 'var(--theme-text-inverse, #0b0f19)',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              🚀 Ingest & Scramble Secrets
            </button>
          </form>

          {ingestStatus && (
            <div
              style={{
                marginTop: '14px',
                padding: '10px',
                backgroundColor: 'var(--theme-bg-subtle, #020617)',
                borderRadius: '6px',
                fontSize: '12px',
                border: '1px solid var(--theme-border, #1e293b)',
              }}
            >
              ℹ️ {ingestStatus}
            </div>
          )}
        </div>

        {/* Right Column: Live Retrieval Playground */}
        <div
          style={{
            backgroundColor: 'var(--theme-bg-card, #151d2a)',
            border: '1px solid var(--theme-border, #1e293b)',
            borderRadius: 'var(--theme-radius-lg, 12px)',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <h2 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>2. Live Retrieval Playground</h2>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Search query..."
              value={testQuery}
              onChange={(e) => setTestQuery(e.target.value)}
              style={{
                flex: 1,
                padding: '8px',
                backgroundColor: 'var(--theme-bg-input, #1e293b)',
                border: '1px solid var(--theme-border, #1e293b)',
                borderRadius: '6px',
                color: 'var(--theme-text-primary, #f8fafc)',
                fontSize: '12px',
              }}
            />
            <button
              onClick={handleTestQuery}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--theme-accent, #38bdf8)',
                color: 'var(--theme-text-inverse, #0b0f19)',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Search
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {searchResults.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--theme-text-muted, #94a3b8)', padding: '24px', fontSize: '12px' }}>
                No search results for current scope query. Click Search to execute test.
              </div>
            ) : (
              searchResults.map((res, i) => (
                <div
                  key={i}
                  style={{
                    padding: '12px',
                    backgroundColor: 'var(--theme-bg-subtle, #020617)',
                    borderRadius: '8px',
                    border: '1px solid var(--theme-border, #1e293b)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--theme-text-primary, #f8fafc)' }}>
                      {res.title}
                    </span>
                    <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 700 }}>
                      Score: {(res.score * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p style={{ fontSize: '11px', color: 'var(--theme-text-secondary, #cbd5e1)', lineHeight: '1.4' }}>
                    {res.text.slice(0, 150)}...
                  </p>
                  <div style={{ marginTop: '6px', fontSize: '10px', color: 'var(--theme-text-muted, #94a3b8)' }}>
                    Scope: <code style={{ color: 'var(--theme-accent, #38bdf8)' }}>{res.scopeId}</code>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
